import { useEffect, useRef } from "react";

/**
 * Keep the Back button inside the app and route it to the home screen.
 *
 * TV browsers (the Philips Vewd browser) and the WebView map hardware Back to
 * *browser history* navigation. With no history to pop, Back unloads the page
 * and the kiosk drops to its launcher - i.e. Back "quits the app". We prevent
 * that by keeping a sentinel history entry: every Back press fires `popstate`
 * (instead of unloading), which we re-arm and route to home. So Back always
 * goes to the main menu and never exits.
 *
 * This complements the keydown path (the APK shell maps its KEYCODE_BACK to an
 * Escape keydown, and `useFocusGrid` treats Escape/Backspace as "back to home").
 * The two cover different surfaces and are both idempotent - landing on home.
 */
export function useBackHome(goHome: () => void): void {
  const goHomeRef = useRef(goHome);
  goHomeRef.current = goHome;
  useEffect(() => {
    window.history.pushState({ foyerBack: true }, "");
    function onPop() {
      // Re-arm a sentinel before doing anything - otherwise the next Back would
      // pop the real entry and unload the page (exit). Then go to the menu.
      window.history.pushState({ foyerBack: true }, "");
      goHomeRef.current();
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
}
